import { forwardRef } from 'react';

import { Table } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';

import { DataTableColumnsSelector } from '@/components/data-table/data-table-columns-selector';
import {
  DataTableFiltersV2,
  TableFilterConfig,
  UseTableFiltersReturn,
} from '@/components/data-table/filters-v2';
import { TableIds } from '@/lib/constants/table-ids';
import {
  DateRangePickerV2,
  DateRangePreset,
  DateRangeV2,
} from '@/modules/common/components/molecules/date/date-range-picker-v2';

import { OverheadListItem } from '../types/overhead';

interface OverheadsToolbarProps {
  /** Sticky top offset (px) — pins right below the collapsed summary chip strip. */
  topPx: number;
  filtersConfig: TableFilterConfig[];
  filterState: UseTableFiltersReturn;
  dates: DateRangeV2 | null;
  selectedPreset: DateRangePreset | null;
  onDateChange: (next: DateRangeV2) => void;
  onPresetSelect: (preset: DateRangePreset) => void;
  onDateReset: () => void;
  table: Table<OverheadListItem>;
  /** "All overheads: N" / "Search results: N" line under the filters. */
  counterText: string;
}

/**
 * The entries title + filters + columns row. Sticky-pinned just below the
 * collapsing summary section. Forwards a ref so the page can measure its
 * height and pin the table column header exactly below it.
 */
export const OverheadsToolbar = forwardRef<HTMLDivElement, OverheadsToolbarProps>(
  (
    {
      topPx,
      filtersConfig,
      filterState,
      dates,
      selectedPreset,
      onDateChange,
      onPresetSelect,
      onDateReset,
      table,
      counterText,
    },
    ref
  ) => {
    const { t } = useTranslation('authenticated.overheads');

    return (
      <div
        ref={ref}
        className="sticky z-20 -mx-4 flex flex-col gap-3 bg-background px-4 pb-4 pt-1"
        style={{ top: topPx }}
      >
        <h2 className="text-lg font-semibold">{t('table.title')}</h2>
        <div className="flex items-start justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <DateRangePickerV2
              dates={dates}
              onChange={onDateChange}
              onPresetSelect={onPresetSelect}
              selectedPreset={selectedPreset}
              onReset={onDateReset}
              defaultPrefix={t('filters.period')}
            />
            <DataTableFiltersV2
              filtersConfig={filtersConfig}
              filterState={filterState}
              tableId={TableIds.OVERHEADS}
              showSaveButton={false}
            />
          </div>
          <DataTableColumnsSelector table={table} />
        </div>
        <p className="text-xs text-muted-foreground">{counterText}</p>
      </div>
    );
  }
);

OverheadsToolbar.displayName = 'OverheadsToolbar';
